import { useCallback } from 'react'

/* ─── Toggle button styles ─── */
const modeActiveStyle = {
  padding: '4px 10px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'var(--font-mono)',
  border: '1px solid var(--labor)',
  background: 'var(--labor-soft)',
  color: 'var(--labor)',
  cursor: 'pointer',
  transition: 'all 0.15s',
  lineHeight: 1
}

const modeInactiveStyle = {
  padding: '4px 10px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'var(--font-mono)',
  border: '1px solid var(--line)',
  background: 'transparent',
  color: 'var(--ink-4)',
  cursor: 'pointer',
  transition: 'all 0.15s',
  lineHeight: 1
}

/* ─── Single material card ─── */
const MaterialCard = ({ mat, index, inputSlots, onChange, onDelete }) => {
  const update = useCallback((patch) => onChange(index, patch), [onChange, index])
  const isFormula = !!mat.formula

  const availableKeys = (inputSlots || []).map(s => s.key).join(', ')

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      {/* Top row: drag handle, label, unit, delete */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Drag handle placeholder */}
        <span className="drag-handle" style={{ flexShrink: 0 }}>
          {'\u2630'}
        </span>

        {/* Label */}
        <input
          className="input-field"
          type="text"
          value={mat.label || ''}
          onChange={e => update({ label: e.target.value })}
          placeholder="e.g. Drip Emitters"
          style={{ flex: 1 }}
        />

        {/* Unit */}
        <input
          className="bare mono"
          type="text"
          value={mat.unit || ''}
          onChange={e => update({ unit: e.target.value })}
          placeholder="EA"
          style={{ width: 60, flexShrink: 0, textAlign: 'center' }}
          title="Unit of measure"
        />

        {/* Delete */}
        <button
          type="button"
          className="row-delete"
          title="Remove material"
          onClick={() => onDelete(index)}
          style={{ flexShrink: 0 }}
        >
          {'\u00D7'}
        </button>
      </div>

      {/* Mode toggle + config row */}
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Mode toggles */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button
            type="button"
            style={isFormula ? modeInactiveStyle : modeActiveStyle}
            onClick={() => update({ formula: null })}
          >
            Per Unit
          </button>
          <button
            type="button"
            style={isFormula ? modeActiveStyle : modeInactiveStyle}
            onClick={() => {
              if (!isFormula) update({ formula: '' })
            }}
          >
            Formula
          </button>
        </div>

        {/* Config area */}
        <div style={{ flex: 1 }}>
          {isFormula ? (
            /* Formula mode */
            <div>
              <input
                className="bare mono"
                type="text"
                value={mat.formula || ''}
                onChange={e => update({ formula: e.target.value })}
                placeholder="e.g. area_sf * (depth_in / 12) / 27"
                style={{ width: '100%' }}
              />
              {availableKeys && (
                <div style={{
                  marginTop: 4,
                  fontSize: 10,
                  color: 'var(--ink-4)',
                  fontFamily: 'var(--font-mono)'
                }}>
                  Available: {availableKeys}
                </div>
              )}
            </div>
          ) : (
            /* Per Unit mode */
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {/* Input slot select */}
              <select
                className="bare"
                value={mat.inputSlotKey || ''}
                onChange={e => update({ inputSlotKey: e.target.value })}
                style={{ minWidth: 140 }}
                title="Input slot"
              >
                <option value="">— slot —</option>
                {(inputSlots || []).map(slot => (
                  <option key={slot.key} value={slot.key}>
                    {slot.label} ({slot.unit})
                  </option>
                ))}
              </select>

              {/* Qty per unit */}
              <input
                className="bare mono"
                type="number"
                min={0}
                step={0.01}
                value={mat.qtyPerUnit ?? 1}
                onChange={e => update({ qtyPerUnit: parseFloat(e.target.value) || 0 })}
                placeholder="1"
                style={{ width: 64, textAlign: 'right' }}
                title="Qty per unit"
              />
              <span style={{ fontSize: 11, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
                per unit
              </span>

              {/* Per class checkbox */}
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                color: mat.perClass ? 'var(--labor)' : 'var(--ink-4)',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap'
              }}>
                <input
                  type="checkbox"
                  checked={!!mat.perClass}
                  onChange={e => update({ perClass: e.target.checked })}
                  style={{ accentColor: 'var(--labor)', cursor: 'pointer' }}
                />
                Per class
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   MaterialsTab
   ═══════════════════════════════════════════════════════════ */
const MaterialsTab = ({ materials, setMaterials, inputSlots, onConfirm }) => {

  const handleChange = useCallback((index, patch) => {
    setMaterials(prev => prev.map((m, i) => i === index ? { ...m, ...patch } : m))
  }, [setMaterials])

  const handleDelete = useCallback((index) => {
    const mat = materials[index]
    const label = mat.label || `Material ${index + 1}`
    onConfirm({
      title: 'Remove Material',
      subtitle: `"${label}"`,
      message: 'This material rule will be permanently removed from the recipe. This action cannot be undone.',
      actionLabel: 'Remove',
      onConfirm: () => {
        setMaterials(prev => prev.filter((_, i) => i !== index))
      }
    })
  }, [materials, setMaterials, onConfirm])

  const handleAdd = useCallback(() => {
    setMaterials(prev => [
      ...prev,
      {
        label: '',
        inputSlotKey: '',
        qtyPerUnit: 1,
        formula: null,
        unit: '',
        perClass: false,
        classFilter: [],
        tags: [],
        isRequired: true
      }
    ])
  }, [setMaterials])

  return (
    <div>
      {/* Section header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16
      }}>
        <div>
          <div className="section-title">
            Material Rules ({materials.length})
          </div>
        </div>
        <button
          type="button"
          className="btn"
          onClick={handleAdd}
          style={{ fontSize: 12, padding: '6px 14px' }}
        >
          + Add Material
        </button>
      </div>

      {/* Empty state */}
      {materials.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{'\u{1F4E6}'}</div>
          <div className="empty-title">No material rules defined</div>
          <div className="empty-subtitle">
            Add a material rule to track quantities of materials used in this service.
          </div>
        </div>
      ) : (
        <div>
          {materials.map((mat, i) => (
            <MaterialCard
              key={i}
              mat={mat}
              index={i}
              inputSlots={inputSlots}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Add button (also shown below cards when non-empty) */}
      {materials.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={handleAdd}>
            + Add Material
          </button>
        </div>
      )}
    </div>
  )
}

export default MaterialsTab
